import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Code, Zap } from 'lucide-react';
import SentimentAnalyzer from './SentimentAnalyzer';

const MicroappCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showSentimentAnalyzer, setShowSentimentAnalyzer] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is md breakpoint
    };

    // Check on initial load
    checkIsMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIsMobile);

    // Cleanup event listener
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  // Sample microapps data - you can replace this with your actual data
  const microapps = {
    '2025-09-23': [
      { 
        id: 1, 
        name: 'AWS Text Sentiment Dashboard', 
        description: 'AI-powered text sentiment analysis using AWS Comprehend', 
        status: 'live',
        url: 'sentiment-analyzer'
      }
    ]
    // '2025-09-23': [
    //   { 
    //     id: 1, 
    //     name: 'Language Helper', 
    //     description: 'AI-powered phrase translator and pronunciation tool using Amazon Translate + Polly', 
    //     status: 'live',
    //     url: 'https://example.com/image-classifier-app'
    //   }
    // ],
    // September 23rd and 25th are future dates - no microapps until those dates arrive
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
    setSelectedDate(null);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
  };

  const handleCloseSentimentAnalyzer = () => {
    console.log('handleCloseSentimentAnalyzer called');
    setShowSentimentAnalyzer(false);
  };

  const handleAppClick = (app) => {
    console.log('App clicked:', app.name, 'URL:', app.url);
    console.log('Current showSentimentAnalyzer state:', showSentimentAnalyzer);
    
    if (app.url === 'sentiment-analyzer') {
      console.log('Setting showSentimentAnalyzer to true');
      setShowSentimentAnalyzer(true);
    } else {
      console.log('Opening external URL:', app.url);
      window.open(app.url, '_blank');
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateStr = formatDate(date);
      const hasMicroapps = microapps[dateStr] && microapps[dateStr].length > 0;
      const isSelected = selectedDate && selectedDate.getDate() === day && 
                        selectedDate.getMonth() === currentDate.getMonth() &&
                        selectedDate.getFullYear() === currentDate.getFullYear();
      const isToday = date.toDateString() === new Date().toDateString();

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-12 rounded-lg border transition-all duration-200 hover:shadow-md ${
            isSelected 
              ? 'bg-blue-500 text-white border-blue-500' 
              : isToday
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : hasMicroapps
              ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
              : 'bg-white border-gray-200 hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center justify-center h-full">
            <span className="text-sm font-medium">{day}</span>
            {hasMicroapps && !isSelected && (
              <div className="w-1 h-1 bg-green-500 rounded-full mt-1"></div>
            )}
          </div>
        </button>
      );
    }

    return days;
  };

  const renderMicroappContent = () => {
    if (!selectedDate) {
      return (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Select a date to view apps</h3>
          <p className="text-gray-500">Click on any date in the calendar above to see what you created that day.</p>
        </div>
      );
    }

    const selectedDateStr = formatDate(selectedDate);
    const selectedDateApps = microapps[selectedDateStr] || [];
    const isPastDate = selectedDate < today;

    if (isPastDate && selectedDateApps.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Code className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No app was created today</h3>
          <p className="text-gray-500">{formatDisplayDate(selectedDate)}</p>
        </div>
      );
    }

    if (selectedDateApps.length === 0) {
      // For future dates, show coming soon message
      if (selectedDate > today) {
        return (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-600 mb-2">App coming soon!</h3>
            <p className="text-gray-500">This app will be available on {formatDisplayDate(selectedDate)}</p>
          </div>
        );
      }
      
      // For today with no microapps
      return (
        <div className="text-center py-12">
          <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Zap className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Ready to create something amazing?</h3>
          <p className="text-gray-500">{formatDisplayDate(selectedDate)}</p>
        </div>
      );
    }

    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Apps created on {formatDisplayDate(selectedDate)}
          </h3>
          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
            {selectedDateApps.length} app{selectedDateApps.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {selectedDateApps.map((app) => (
            <div 
              key={app.id} 
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-blue-300 min-h-[140px] flex flex-col"
              onClick={() => handleAppClick(app)}
            >
              <div className="flex items-start justify-between mb-4">
                <h4 className="font-semibold text-gray-900 text-lg leading-tight pr-4 flex-1">
                  {app.name}
                </h4>
                { isMobile ?
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                    app.status === 'demo' ? 'bg-orange-100 text-orange-700' :
                    app.status === 'live' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {app.status.toUpperCase()}
                  </span>
                  :
                  <span />
                }
              </div>
              <p className="text-gray-600 text-sm mb-4 flex-1 leading-relaxed">{app.description}</p>
              <div className="flex items-center text-blue-600 text-sm font-medium mt-auto">
                <span>Click to {app.status === 'demo' ? 'watch demo' : 'use app'}</span>
                <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Show Sentiment Analyzer Modal */}
      {showSentimentAnalyzer && (
        <SentimentAnalyzer onClose={handleCloseSentimentAnalyzer} />
      )}
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Daily AI Apps</h1>
          <p className="text-lg text-gray-600">Daily AI projects powered by cloud technologies</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar Widget */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="h-8 flex items-center justify-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {renderCalendar()}
            </div>
            
            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-50 border border-blue-300 rounded"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-50 border border-green-300 rounded"></div>
                <span className="text-gray-600">Has apps</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-600">Selected</span>
              </div>
            </div>
          </div>

          {/* Microapp Display */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {renderMicroappContent()}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-gray-600">
              Questions or feedback? Contact me at{' '}
              <a 
                href="mailto:nathandrake@example.com" 
                className="text-blue-600 hover:text-blue-700 underline"
              >
                nathan@tambeta.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MicroappCalendar;